import React, { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { Bot, X, Send, Loader2, Sparkles } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const STORAGE_KEY = "clap-ai-session";
const HISTORY_KEY = "clap-ai-history";
const MAX_INPUT = 500;
const MAX_HISTORY_KEEP = 8; // keep last 8 messages locally for display

const GREETING = {
    role: "assistant",
    content:
        "Hi 👋 I'm Clap AI. Ask me anything about IdeaClap — capabilities, talent network, or what we can build for you.",
};

// Token-minimal floating chatbot. Keeps history short on send (last 4 turns
// replayed to backend), input capped at 500 chars, response capped at ~220
// output tokens server-side.
export const ClapAI = () => {
    const [open, setOpen] = useState(false);
    const [messages, setMessages] = useState([GREETING]);
    const [input, setInput] = useState("");
    const [busy, setBusy] = useState(false);
    const [sessionId, setSessionId] = useState(null);
    const scrollRef = useRef(null);
    const inputRef = useRef(null);

    // Load persisted session + recent history
    useEffect(() => {
        try {
            const sid = localStorage.getItem(STORAGE_KEY);
            if (sid) setSessionId(sid);
            const raw = localStorage.getItem(HISTORY_KEY);
            if (raw) {
                const parsed = JSON.parse(raw);
                if (Array.isArray(parsed) && parsed.length) {
                    setMessages([GREETING, ...parsed.slice(-MAX_HISTORY_KEEP)]);
                }
            }
        } catch {
            /* ignore */
        }
    }, []);

    // Persist history on change
    useEffect(() => {
        try {
            const toStore = messages.filter((m) => m !== GREETING);
            localStorage.setItem(HISTORY_KEY, JSON.stringify(toStore.slice(-MAX_HISTORY_KEEP)));
        } catch {
            /* ignore */
        }
    }, [messages]);

    // Auto-scroll to bottom on new message
    useEffect(() => {
        const el = scrollRef.current;
        if (el) el.scrollTop = el.scrollHeight;
    }, [messages, open]);

    // Focus input when panel opens
    useEffect(() => {
        if (open) setTimeout(() => inputRef.current?.focus(), 80);
    }, [open]);

    const send = async (e) => {
        e?.preventDefault();
        const text = input.trim();
        if (!text || busy) return;
        const userMsg = { role: "user", content: text.slice(0, MAX_INPUT) };
        const next = [...messages, userMsg];
        setMessages(next);
        setInput("");
        setBusy(true);
        try {
            const trimmedHistory = next
                .filter((m) => m !== GREETING)
                .slice(-8) // last 8 messages
                .map((m) => ({ role: m.role, content: m.content }));
            const { data } = await axios.post(`${API}/chat`, {
                message: text,
                session_id: sessionId,
                history: trimmedHistory,
            });
            const reply = (data?.reply || "").trim() || "Sorry, I didn't catch that.";
            if (data?.session_id && data.session_id !== sessionId) {
                setSessionId(data.session_id);
                try {
                    localStorage.setItem(STORAGE_KEY, data.session_id);
                } catch {
                    /* ignore */
                }
            }
            setMessages((prev) => [...prev, { role: "assistant", content: reply }]);
        } catch (err) {
            const detail = err?.response?.data?.detail;
            setMessages((prev) => [
                ...prev,
                {
                    role: "assistant",
                    content:
                        typeof detail === "string"
                            ? detail
                            : "Hmm, I couldn't reach the model. Try again in a moment.",
                },
            ]);
        } finally {
            setBusy(false);
        }
    };

    const onKeyDown = (e) => {
        if (e.key === "Enter" && !e.shiftKey) {
            e.preventDefault();
            send();
        }
    };

    const charLeft = useMemo(() => MAX_INPUT - input.length, [input]);

    return (
        <>
            {/* Floating launcher button — placed above the Emergent badge */}
            <motion.button
                type="button"
                onClick={() => setOpen((v) => !v)}
                aria-label={open ? "Close Clap AI" : "Open Clap AI"}
                data-testid="clap-ai-toggle"
                initial={{ opacity: 0, y: 20, scale: 0.9 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                transition={{ delay: 0.4, duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
                className="fixed right-4 sm:right-5 z-[60] inline-flex items-center gap-2 rounded-full bg-[hsl(var(--text))] hover:bg-[hsl(var(--text))]/92 text-[hsl(var(--bg))] pl-3 pr-4 py-2.5 shadow-deep ring-1 ring-black/10 transition-colors"
                style={{ bottom: "72px" }}
            >
                <span className="relative inline-flex h-7 w-7 items-center justify-center rounded-full bg-[hsl(var(--gold))] text-[hsl(var(--bg))]">
                    <Bot className="h-3.5 w-3.5" />
                    {!open && (
                        <span className="absolute -top-0.5 -right-0.5 h-2 w-2 rounded-full bg-[hsl(var(--gold))] ring-2 ring-[hsl(var(--text))] pulse-dot" />
                    )}
                </span>
                <span className="text-[13px] font-semibold tracking-tight">
                    {open ? "Close" : "Clap AI"}
                </span>
            </motion.button>

            {/* Chat panel */}
            <AnimatePresence>
                {open && (
                    <motion.div
                        key="clap-ai-panel"
                        initial={{ opacity: 0, y: 16, scale: 0.98 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        exit={{ opacity: 0, y: 12, scale: 0.98 }}
                        transition={{ duration: 0.32, ease: [0.16, 1, 0.3, 1] }}
                        className="fixed right-4 sm:right-5 z-[59] w-[min(92vw,380px)] rounded-3xl bg-white border border-black/10 shadow-deep overflow-hidden flex flex-col"
                        style={{ bottom: "128px", maxHeight: "min(74vh, 620px)" }}
                        data-testid="clap-ai-panel"
                        role="dialog"
                        aria-label="Clap AI chat"
                    >
                        <header className="flex items-center justify-between gap-3 p-3.5 border-b border-black/[0.08] bg-[hsl(var(--bg-2))]/70">
                            <div className="flex items-center gap-2.5 min-w-0">
                                <span className="relative inline-flex h-9 w-9 items-center justify-center rounded-full bg-[hsl(var(--text))] text-[hsl(var(--bg))]">
                                    <Bot className="h-4 w-4" />
                                    <span className="absolute -bottom-0 -right-0 h-2.5 w-2.5 rounded-full bg-[hsl(var(--gold))] ring-2 ring-white" />
                                </span>
                                <div className="min-w-0">
                                    <div className="flex items-center gap-1.5">
                                        <span className="font-display text-[16px] text-[hsl(var(--text))] tracking-[-0.01em]">
                                            Clap AI
                                        </span>
                                        <Sparkles className="h-3 w-3 text-[hsl(var(--gold))]" />
                                    </div>
                                    <div className="text-[11px] text-[hsl(var(--text-subtle))] font-mono uppercase tracking-[0.16em]">
                                        IdeaClap assistant
                                    </div>
                                </div>
                            </div>
                            <button
                                onClick={() => setOpen(false)}
                                aria-label="Close"
                                className="inline-flex h-8 w-8 items-center justify-center rounded-full ring-1 ring-black/10 hover:bg-[hsl(var(--bg-2))] text-[hsl(var(--text-muted))]"
                                data-testid="clap-ai-close"
                            >
                                <X className="h-4 w-4" />
                            </button>
                        </header>

                        <div
                            ref={scrollRef}
                            className="flex-1 min-h-0 overflow-y-auto px-3.5 py-4 space-y-3 bg-white"
                            data-testid="clap-ai-messages"
                        >
                            {messages.map((m, idx) => (
                                <Bubble key={idx} role={m.role} content={m.content} />
                            ))}
                            {busy && (
                                <Bubble role="assistant" content={null} typing />
                            )}
                        </div>

                        <form
                            onSubmit={send}
                            className="p-3 border-t border-black/[0.08] bg-[hsl(var(--bg-2))]/60"
                            data-testid="clap-ai-form"
                        >
                            <div className="flex items-end gap-2 rounded-2xl bg-white ring-1 ring-inset ring-black/10 focus-within:ring-2 focus-within:ring-[hsl(var(--blue))] px-3 py-2">
                                <textarea
                                    ref={inputRef}
                                    rows={1}
                                    value={input}
                                    onChange={(e) =>
                                        setInput(e.target.value.slice(0, MAX_INPUT))
                                    }
                                    onKeyDown={onKeyDown}
                                    placeholder="Ask about capabilities, talent, pricing…"
                                    className="flex-1 resize-none bg-transparent text-[14px] text-[hsl(var(--text))] placeholder:text-[hsl(var(--text-subtle))] outline-none max-h-32 leading-relaxed py-1"
                                    data-testid="clap-ai-input"
                                    aria-label="Message Clap AI"
                                />
                                <button
                                    type="submit"
                                    disabled={!input.trim() || busy}
                                    aria-label="Send"
                                    data-testid="clap-ai-send"
                                    className="shrink-0 inline-flex h-9 w-9 items-center justify-center rounded-full bg-[hsl(var(--text))] hover:bg-[hsl(var(--text))]/92 text-[hsl(var(--bg))] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                                >
                                    {busy ? (
                                        <Loader2 className="h-4 w-4 animate-spin" />
                                    ) : (
                                        <Send className="h-3.5 w-3.5" />
                                    )}
                                </button>
                            </div>
                            <div className="flex items-center justify-between mt-1.5 px-1 text-[10.5px] text-[hsl(var(--text-subtle))] font-mono uppercase tracking-[0.16em]">
                                <span>Powered by AI · Replies vary</span>
                                <span>{charLeft}</span>
                            </div>
                        </form>
                    </motion.div>
                )}
            </AnimatePresence>
        </>
    );
};

const Bubble = ({ role, content, typing = false }) => {
    const isUser = role === "user";
    return (
        <div className={`flex ${isUser ? "justify-end" : "justify-start"}`}>
            <div
                className={`max-w-[85%] rounded-2xl px-3.5 py-2.5 text-[14px] leading-[1.55] ${
                    isUser
                        ? "bg-[hsl(var(--text))] text-[hsl(var(--bg))]"
                        : "bg-[hsl(var(--bg-2))] text-[hsl(var(--text))] ring-1 ring-black/[0.06]"
                }`}
            >
                {typing ? (
                    <span className="inline-flex items-center gap-1">
                        <Dot delay="0s" />
                        <Dot delay="0.18s" />
                        <Dot delay="0.36s" />
                    </span>
                ) : (
                    <span className="whitespace-pre-wrap break-words">{content}</span>
                )}
            </div>
        </div>
    );
};

const Dot = ({ delay }) => (
    <span
        className="inline-block h-1.5 w-1.5 rounded-full bg-[hsl(var(--text-subtle))]/70 animate-bounce"
        style={{ animationDelay: delay, animationDuration: "1s" }}
    />
);

export default ClapAI;
